import { useRef, type ReactNode } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import "./marketing.css";

type MarketingDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  /** 560px for the waitlist form, 440px for Sign In. */
  size?: "default" | "narrow";
  children: ReactNode;
};

/**
 * Where focus goes when the element that opened a dialog no longer exists —
 * a button in the mobile menu sheet (which closes as the dialog opens), or the
 * Sign In dialog's "Join the waitlist" handoff. The first visible match wins.
 */
const FOCUS_FALLBACKS = [
  ".mk-header__toggle",
  ".mk-header__actions .mk-btn--line",
];

function focusFallback(): boolean {
  for (const selector of FOCUS_FALLBACKS) {
    const el = document.querySelector<HTMLElement>(selector);
    if (el && el.offsetParent !== null) {
      el.focus();
      return true;
    }
  }
  return false;
}

/**
 * The marketing dialog shell: a paper panel on a 52% ink scrim with a 44px
 * close button. Radix owns the behaviour — focus trap, Escape, outside click,
 * focus return to the trigger and scroll lock — and the overlay is the scroll
 * container, so a tall form scrolls within the viewport on a short phone.
 */
const MarketingDialog = ({
  open,
  onOpenChange,
  title,
  description,
  size = "default",
  children,
}: MarketingDialogProps) => {
  // The element focused when the dialog opened. Radix returns focus to it on
  // close; when it has since left the page, focusFallback() picks the header.
  const opener = useRef<Element | null>(null);
  const wasOpen = useRef(false);
  if (open && !wasOpen.current && typeof document !== "undefined")
    opener.current = document.activeElement;
  wasOpen.current = open;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="mk-dialog-overlay">
          <DialogPrimitive.Content
            className={cn(
              "mk-dialog",
              size === "narrow" && "mk-dialog--narrow",
            )}
            {...(description ? {} : { "aria-describedby": undefined })}
            onCloseAutoFocus={(event) => {
              const el = opener.current;
              if (el && el !== document.body && el.isConnected) return;
              if (focusFallback()) event.preventDefault();
            }}
          >
            <div className="mk-dialog__head">
              <div className="mk-dialog__titles">
                <DialogPrimitive.Title className="mk-dialog__title">
                  {title}
                </DialogPrimitive.Title>
                {description && (
                  <DialogPrimitive.Description className="mk-dialog__desc">
                    {description}
                  </DialogPrimitive.Description>
                )}
              </div>
            </div>
            {children}
            {/* Last in DOM order so opening focuses the first field (or the
                Google button), not Close; positioned top-right by CSS. */}
            <DialogPrimitive.Close
              className="mk-dialog__close"
              aria-label="Close"
            >
              <X size={20} aria-hidden="true" />
            </DialogPrimitive.Close>
          </DialogPrimitive.Content>
        </DialogPrimitive.Overlay>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export default MarketingDialog;
